import useAuth from '@app/auth/use-auth';
import { PACKAGING_ITEMS_RATING } from '@app/ccf/company-report/utils';
import { useGetFilterLabels } from '@app/components/data-table/hooks/use-get-filter-labels';
import PreviewSheet from '@app/components/preview/preview-sheet';
import { PreviewSheetBarChart } from '@app/components/preview/preview-sheet-bar-chart';
import PreviewSheetSection from '@app/components/preview/preview-sheet-chart-section';
import { useAccount } from '@shared/api';
import { useGetPurchasedPackagingPreview } from '@shared/api/hooks/purchased-goods/packaging';
import { itemsByIntensityChartConfig } from '@shared/components/chart/configs';
import { PurchasedGoodsPackagingPreviewRetrieveParams } from '@shared/api/types';
import SimplePieChart from '@shared/components/chart/simple-pie-chart';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import { ChartConfig } from '@shared/components/ui/chart';
import { formatCompact, getCurrencySymbol } from '@shared/lib/format';
import { capitalizeFirstLetter, formatNumber } from '@shared/lib/utils';
import { useQueryStates } from 'nuqs';
import { useState } from 'react';
import { packagingFilters } from '../filters';

export default function PurchasedPackagingPreviewSheet() {
  const [isOpen, setIsOpen] = useState(false);

  const { flatLabels: tags } = useGetFilterLabels(
    Object.values(packagingFilters).map((f) => f.column)
  );

  // Retrieve query params from URL
  const schema = Object.fromEntries(
    Object.values(packagingFilters).map((f) => [f.column, f.type])
  );
  const [query] = useQueryStates(schema);

  // Map query params to API params
  const params: PurchasedGoodsPackagingPreviewRetrieveParams = {};
  Object.values(packagingFilters).map(({ apiKey, column }) => {
    params[apiKey] = query[column] || undefined;
  });

  const { data, status } = useGetPurchasedPackagingPreview({
    params,
    enabled: true,
  });

  const { user } = useAuth();
  const hasCostFeature = !!user?.features.cost;
  const { data: account } = useAccount(true);

  const totalEmissionsTonnes = data?.totalEmissionsTonnes;
  const totalWeightKg = data?.totalWeightKg;
  const totalSpend = data?.totalSpend != null ? Number(data.totalSpend) : null;

  const emissionsByMaterial = data?.emissionsByMaterial?.map((category) => {
    return {
      material: capitalizeFirstLetter(category.material),
      emissions: category.totalEmissionsTonnes,
      fill: 'var(--chart-3)',
    };
  });

  const itemsByRating = data?.itemsByCarbonIntensity?.map((category) => {
    return {
      category: category.carbonIntensityCategory,
      items: category.numberOfItems,
      fill: PACKAGING_ITEMS_RATING[
        category.carbonIntensityCategory as keyof typeof PACKAGING_ITEMS_RATING
      ].color,
    };
  });

  const numberOfItems = data?.itemsByCarbonIntensity?.reduce(
    (acc, category) => {
      return acc + category.numberOfItems;
    },
    0
  );
  const chartConfig = {
    emissions: {
      label: 'Emissions',
      color: 'hsl(var(--chart-1))',
      unit: 'tCO2e',
    },
  } satisfies ChartConfig;

  return (
    <PreviewSheet
      title="Packaging items"
      status={status}
      metrics={[
        {
          title: 'Total emissions',
          value: formatNumber(totalEmissionsTonnes!),
          unit: 'tCO2e',
        },
        {
          title: 'Total weight of items',
          value: formatNumber(totalWeightKg! / 1000),
          unit: 't',
        },
        ...(hasCostFeature && totalSpend != null
          ? [
              {
                title: 'Total spend',
                value: (
                  <>
                    {/* Currency symbol matches the gray-400 unit colour so it
                        reads as a unit rather than blending into the figure. */}
                    <span className="mr-1 text-gray-400">
                      {getCurrencySymbol(account?.defaultCurrency)}
                    </span>
                    {formatCompact(totalSpend)}
                  </>
                ),
                unit: '',
              },
            ]
          : []),
      ]}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      tags={tags}
    >
      {data && (
        <>
          <PreviewSheetSection title="Items by carbon intensity">
            <div className="flex items-center space-y-2">
              <SimplePieChart
                dataKey="items"
                nameKey="category"
                chartConfig={itemsByIntensityChartConfig}
                chartData={itemsByRating!.reverse()}
                innerRadius={50}
                className="h-40 w-48"
                margin={{ top: 0, right: 0, bottom: 0, left: -0 }}
                centerLabel={{
                  value: numberOfItems!,
                  label: 'items',
                }}
              />
              <div className="flex flex-col items-start justify-start gap-y-2">
                {(
                  ['Low', 'Medium', 'High'] as Array<
                    keyof typeof PACKAGING_ITEMS_RATING
                  >
                ).map((rating, idx) => {
                  return (
                    <div key={idx} className="flex items-start gap-2">
                      <div
                        className="mt-0.5 size-4 rounded-sm"
                        style={{
                          backgroundColor: PACKAGING_ITEMS_RATING[rating].color,
                        }}
                      ></div>
                      <div>
                        <Text className="mt-0">{rating}</Text>{' '}
                        <span className="flex items-baseline space-x-1">
                          <Text variant="unit">
                            {PACKAGING_ITEMS_RATING[rating].threshold}
                          </Text>
                          <Unit variant="kgCO2e/kg" />
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </PreviewSheetSection>

          {/* One bar says nothing the totals above don't; the breakdown
              needs at least two buckets to be worth the space. */}
          {(emissionsByMaterial?.length ?? 0) > 1 && (
            <PreviewSheetSection
              title="Emissions by material"
              unit="tCO2e"
              className="h-full"
            >
              <PreviewSheetBarChart
                nameKey="material"
                chartData={emissionsByMaterial}
                chartConfig={chartConfig}
              />
            </PreviewSheetSection>
          )}
        </>
      )}
    </PreviewSheet>
  );
}
